import { AxiosError } from "axios";
import PageOptions from "model/dto/pageOptions";
import { GetBlogsRequest } from "model/request/blog/getBlogsRequest";
import { GetBlogResponseItem } from "model/response/blog/getBlogsResponse";
import { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";

//SERVICES
import { createBlogService } from "services/blogService";

//MODELS
interface BlogContextProps {
  children: React.ReactNode;
}

type BlogContextInterface = {
  blogs: GetBlogResponseItem[];
  blogsIsLoading: boolean;
  fetchBlogs: () => Promise<void>;
  pageOptions: PageOptions;
  setPageOptions: (pageOptions: PageOptions) => void;
  tooManyRequests: boolean;
};

export const BlogContext = createContext<BlogContextInterface | undefined>(
  undefined,
);

export const useBlogContext = () => {
  const context = useContext(BlogContext);
  if (context === undefined) {
    // This error will only be thrown if the hook is used outside of the FilterProvider
    throw new Error("useBlogContext must be used within a BlogProvider");
  }
  return context;
};

export const BlogProvider = ({ children }: BlogContextProps) => {
  const [blogs, setBlogs] = useState<GetBlogResponseItem[]>([]);
  const [pageOptions, setPageOptions] = useState<PageOptions>({
    page: 0,
    hasNextPage: false,
  });
  const [pageSize, setPageSize] = useState(24);
  const [blogsIsLoading, setBlogsIsLoading] = useState(true);
  const [tooManyRequests, setTooManyRequests] = useState(false);

  const blogService = createBlogService();

  const fetchBlogs = async () => {
    const req: GetBlogsRequest = {
      take: pageSize,
      skip: pageOptions.page * pageSize,
    };
    try {
      setBlogsIsLoading(true);
      const response = await blogService.getBlogs(req);
      setBlogs(response.data);
      setTooManyRequests(false);
      setPageOptions((prev) => {
        return {
          ...prev,
          hasNextPage: response.rowCount > pageSize * (prev.page + 1),
        };
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 429) {
          setTooManyRequests(true);
        }
      }
      console.error(error);
      toast.error("An error occurred while fetching blogs");
    } finally {
      setBlogsIsLoading(false);
    }
  };

  const contextValue = {
    blogs,
    blogsIsLoading,
    fetchBlogs,
    pageOptions,
    setPageOptions,
    tooManyRequests,
  };

  return (
    <BlogContext.Provider value={contextValue}>{children}</BlogContext.Provider>
  );
};

export default BlogContext;
